//
// # useSchematicDisplayInfo Hook
//
// Watches the active editor and extracts schematic label visibility state.
// Returns the display options object and a toggle callback when a schematic is active.
//
// Re-computes whenever the active ident changes or content is reloaded.
//

import { useCallback, useEffect, useState } from "react";
import {
  type Schematic,
  type SchematicDisplayOptions,
  type SchematicSelectability,
} from "EditorCore";
import { useWorkspace, useWorkspaceSnapshot } from "../../context";

export interface SchematicDisplayInfo {
  // Whether the active editor is a schematic
  readonly isSchematic: boolean;
  // Current display options, or null when not a schematic
  readonly options: SchematicDisplayOptions | null;
  // Toggle a single display option by key
  readonly toggle: (key: keyof SchematicDisplayOptions) => void;
  // Per-category selectability flags, or null when not a schematic
  readonly selectability: SchematicSelectability | null;
  // Toggle a single selectability flag by category key
  readonly toggleSelectability: (key: keyof SchematicSelectability) => void;
}

export function useSchematicDisplayInfo(): SchematicDisplayInfo {
  const workspace = useWorkspace();
  const snapshot = useWorkspaceSnapshot();
  const activeIdent = snapshot.activeIdent;
  const contentVersion = snapshot.contentVersion;

  // Increment to force a re-render after in-place display option mutations.
  const [, setRenderTick] = useState(0);

  const [schematic, setSchematic] = useState<Schematic | null>(null);

  useEffect(() => {
    if (!activeIdent) {
      setSchematic(null);
      return;
    }

    const editor = workspace.getActiveEditor();
    if (!editor || editor.content.contentKind !== "schematic") {
      setSchematic(null);
      return;
    }

    setSchematic(editor.content as Schematic);
  }, [activeIdent, contentVersion, workspace]);

  const toggle = useCallback(
    (key: keyof SchematicDisplayOptions) => {
      if (!schematic) return;
      schematic.toggleSchematicDisplay(key);
      setRenderTick((v) => v + 1);
    },
    [schematic]
  );

  const toggleSelectability = useCallback(
    (key: keyof SchematicSelectability) => {
      if (!schematic) return;
      schematic.toggleSelectability(key);
      setRenderTick((v) => v + 1);
    },
    [schematic]
  );

  // Read display options from the schematic on each render
  // (including when renderTick changes after a toggle)
  const options = schematic?.schematicDisplay ?? null;
  const selectability = schematic?.selectability ?? null;

  return {
    isSchematic: schematic !== null,
    options,
    toggle,
    selectability,
    toggleSelectability,
  };
}
